import { useEffect } from 'react';
import { isTauri } from '../utils/tauri';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/apiService';
import {
  setShareAcceptToken,
  setNetworkImportSlug,
  setJoinDeepLinkCode,
  setTeamsModalMode,
  setShowTeamsModal,
  setResetDeepLink,
  setAuthModalMode,
  setAuthModalOpen,
  setPendingTeamInviteToken,
  setUpgradeModalOpen,
  setPlanModalOpen,
} from '../store/uiModalStore';

/**
 * Wires Tauri deep-link events (apiboo://share|network|join|reset-password|
 * team-invite, plus billing-upgrade-completed) to uiModalStore. Pure effect hook.
 *
 * @param {{ showToast: Function }} deps
 */
export function useDeepLinks({ showToast }) {
  useEffect(() => {
    if (!isTauri()) return undefined;
    // listenSafe returns idempotent unlistens; the `disposed` flag closes the
    // StrictMode race where cleanup runs while registration is still in
    // flight (late-resolving listeners are unregistered instead of leaking).
    let disposed = false;
    const unlistens = [];
    const track = (fn) => {
      if (disposed) fn();
      else unlistens.push(fn);
      return fn;
    };
    (async () => {
      try {
        const { listenSafe } = await import('../utils/safeTauriEvent');
        const listen = async (event, handler) => track(await listenSafe(event, handler));
        await listen('share-link-opened', (event) => {
          const token = event.payload;
          if (token) setShareAcceptToken(token);
        });
        await listen('network-link-opened', (event) => {
          const slug = event.payload;
          if (slug) setNetworkImportSlug(slug);
        });
        await listen('join-code-opened', (event) => {
          const code = event.payload;
          if (code) {
            setJoinDeepLinkCode(code);
            setTeamsModalMode('join');
            setShowTeamsModal(true);
          }
        });
        await listen('reset-password-opened', (event) => {
          const { token, email } = event.payload || {};
          if (token && email) {
            setResetDeepLink({ token, email });
            setAuthModalMode('reset');
            setAuthModalOpen(true);
          }
        });
        // Documents and boards (apiboo://documents|boards/…) are not events
        // any more: Rust queues them and deepLinks/contentLinks.js opens them
        // once the user is signed in (content_link.rs). The board listener
        // used to be registered twice, so a board link opened two tabs' worth.
        await listen('team-invite-opened', (event) => {
          const token = event.payload;
          if (token) setPendingTeamInviteToken(token);
        });
        await listen('billing-upgrade-completed', () => {
          setUpgradeModalOpen(false);
          setPlanModalOpen(false);
          const planBefore = useAuthStore.getState().user?.plan ?? 'free';
          let attempts = 0;
          const poll = () => {
            authService.getUser()
              .then((updatedUser) => {
                useAuthStore.setState({ user: updatedUser });
                if (updatedUser.plan && updatedUser.plan !== planBefore) {
                  showToast('Subscription activated! Welcome to your new plan.', 'success');
                } else if (++attempts < 6) {
                  setTimeout(poll, 2000);
                }
              })
              .catch(() => { if (++attempts < 6) setTimeout(poll, 2000); });
          };
          setTimeout(poll, 1500);
        });
      } catch (e) {
        console.warn('Deep link listener setup failed:', e);
      }
    })();
    return () => {
      disposed = true;
      for (const fn of unlistens) fn();
      unlistens.length = 0;
    };
  }, [showToast]);
}
